"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import type { MintingState } from "./hooks";
import { WalletConnectionInline } from "./WalletConnectionInline";

// ============================================================================
// Types
// ============================================================================

export interface MintingSectionProps {
  /** Current minting state from useMintingMachine */
  mintingState: MintingState;
  /** Callback when user clicks mint button */
  onMint: () => void;
  /** Callback when user clicks retry button */
  onRetry: () => void;
  /** Callback when user connects a wallet inline */
  onWalletConnected?: (accountName: string) => void;
  /** Optional image URL for the asset preview */
  assetImage?: string;
  /** Optional video URL for the asset preview (takes priority over image) */
  assetVideo?: string;
  /** Title for asset (used in alt text) */
  assetTitle: string;
  /** Primary color for whitelabel styling */
  primaryColor?: string;
  /** Campaign chain type (WAX, POAP, etc.) */
  chain?: string;
}

// ============================================================================
// Sub-components
// ============================================================================

export interface AssetCardProps {
  message: string;
  assetImage?: string;
  assetVideo?: string;
  assetTitle: string;
  isPoap?: boolean;
}

export function AssetCard({ message, assetImage, assetVideo, assetTitle, isPoap }: AssetCardProps) {
  const imageShape = isPoap ? "rounded-full" : "rounded-lg";

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
      <div className="p-6">
        <div className="text-center">
          <div className={`mx-auto w-48 h-48 bg-gray-100 ${imageShape} flex items-center justify-center mb-4 overflow-hidden relative`}>
            {assetVideo ? (
              <video
                src={assetVideo}
                className="w-full h-full object-contain"
                muted
                loop
                playsInline
                autoPlay
                crossOrigin="anonymous"
              />
            ) : assetImage ? (
              <Image
                src={assetImage}
                alt={`${assetTitle} Digital Asset`}
                className="object-contain"
                fill
                unoptimized
              />
            ) : (
              <div className="text-4xl">&#127873;</div>
            )}
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {assetTitle || 'Exclusive Digital Asset'}
          </h3>
          <p className="text-gray-600 mb-4">{message}</p>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Status Components
// ============================================================================

function SuccessStatus({ mintingState, chain }: { mintingState: MintingState; chain?: string }) {
  const isPoap = chain === 'POAP';
  // For POAP, the message depends on the recipient: a wallet address mints
  // (more or less) directly, but an email is a *reservation* in POAP's system —
  // the user must open POAP's email and finish on POAP's side. Connecting an
  // ETH address here later does NOT redirect an already-emailed POAP.
  const poapRecipient = usePoapRecipient(isPoap);
  let poapMessage: string;
  if (poapRecipient?.type === 'ETH') {
    poapMessage = `Your POAP is being sent to your wallet (${poapRecipient.value}).`;
  } else if (poapRecipient?.type === 'Email') {
    poapMessage = `We've emailed your POAP to ${poapRecipient.value}. Open that email to finish claiming it on POAP — you'll sign in to (or create) your POAP account and connect a wallet there.`;
  } else {
    poapMessage = 'Your POAP is on its way! If you claimed with an email address, check your inbox to finish claiming it on POAP.';
  }
  return (
    <div className="bg-green-50 rounded-lg border-l-4 border-green-400 p-6">
      <div className="text-center">
        <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-green-800 mb-2">Successfully Claimed!</h3>
        <p className="text-green-700 mb-4">
          {isPoap ? poapMessage : 'Your NFT has been minted to your WAX wallet.'}
        </p>
        {mintingState.mintedAssetId && (
          <p className="text-sm text-green-600 mb-2">
            Asset ID: {mintingState.mintedAssetId}
          </p>
        )}
        <Link
          href="/my-assets"
          className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
        >
          View my Digital Asset
          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </Link>
      </div>
    </div>
  );
}

function FailedStatus({
  mintingState,
  onRetry
}: {
  mintingState: MintingState;
  onRetry: () => void;
}) {
  return (
    <div className="bg-red-50 rounded-lg border-l-4 border-red-400 p-6">
      <div className="text-center">
        <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-red-800 mb-2">Minting Failed</h3>
        <p className="text-red-700 mb-4">
          {mintingState.errorMessage || 'An error occurred during minting.'}
        </p>
        {mintingState.retryCount !== undefined && mintingState.retryCount < 3 && (
          <p className="text-sm text-red-600 mb-4">
            Retry attempts: {mintingState.retryCount}/3
          </p>
        )}
        {mintingState.canRetry && (
          <button
            onClick={onRetry}
            className="px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
          >
            Retry Minting
          </button>
        )}
      </div>
    </div>
  );
}

function ReservedStatus({ mintingState, chain }: { mintingState: MintingState; chain?: string }) {
  const isPoap = chain === 'POAP';
  return (
    <div className="bg-amber-50 rounded-lg border-l-4 border-amber-400 p-6">
      <div className="text-center">
        <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-amber-800 mb-2">Reserved for You</h3>
        <p className="text-amber-700 mb-4">
          {isPoap
            ? "Your POAP is reserved. Add your ETH address or POAP email in settings to complete the claim."
            : "Your digital asset is reserved. Connect a WAX wallet to mint it."}
        </p>
        {mintingState.claimId && (
          <p className="text-xs text-amber-600 mb-4 font-mono">
            Claim ID: {mintingState.claimId}
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <Link
            href="/dashboard/settings"
            className="inline-flex items-center justify-center px-6 py-3 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors"
          >
            Connect Wallet
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
          <Link
            href="/my-assets"
            className="inline-flex items-center justify-center px-6 py-3 bg-white border border-amber-300 text-amber-700 rounded-lg font-medium hover:bg-amber-100 transition-colors"
          >
            View My Assets
          </Link>
        </div>
      </div>
    </div>
  );
}

function FinalizingStatus({ chain }: { chain?: string }) {
  const isPoap = chain === 'POAP';
  return (
    <div className="bg-blue-50 rounded-lg border-l-4 border-blue-400 p-6">
      <div className="text-center">
        <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-blue-800 mb-2">Claim Confirmed!</h3>
        <p className="text-blue-700 mb-4">
          {isPoap
            ? "Your POAP was claimed and is finalizing on the POAP network. It'll appear in your assets shortly — no need to claim again."
            : "Your asset was claimed and is finalizing on the blockchain. It'll appear in your assets shortly — no need to mint again."}
        </p>
        <Link
          href="/my-assets"
          className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
        >
          View my Digital Asset
          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </Link>
      </div>
    </div>
  );
}

function WalletRequiredStatus({ mintingState }: { mintingState: MintingState }) {
  return (
    <div className="bg-yellow-50 rounded-lg border-l-4 border-yellow-400 p-6">
      <div className="text-center">
        <div className="mx-auto w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-yellow-800 mb-2">WAX Wallet Required</h3>
        <p className="text-yellow-700 mb-4">
          {mintingState.errorMessage || 'Please connect your WAX wallet to claim this digital asset.'}
        </p>
        <Link
          href="/dashboard/settings"
          className="inline-flex items-center px-6 py-3 bg-yellow-600 text-white rounded-lg font-medium hover:bg-yellow-700 transition-colors"
        >
          Connect WAX Wallet
          <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </Link>
      </div>
    </div>
  );
}

interface ProcessingStatusProps {
  mintingState: MintingState;
  assetImage?: string;
  assetVideo?: string;
  assetTitle: string;
  chain?: string;
}

function ProcessingStatus({ mintingState, assetImage, assetVideo, assetTitle, chain }: ProcessingStatusProps) {
  const isPoap = chain === 'POAP';
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="text-center">
        <AssetCard
          message={isPoap ? "Claiming your POAP..." : "Minting your NFT..."}
          assetImage={assetImage}
          assetVideo={assetVideo}
          assetTitle={assetTitle}
          isPoap={isPoap}
        />
        <div className="mt-6">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {mintingState.status === 'claiming' ? 'Initiating Claim...' : 'Minting Your NFT...'}
          </h3>
          <p className="text-gray-600">
            {mintingState.status === 'polling'
              ? 'This is taking longer than expected. Please wait...'
              : 'Please wait while we mint your exclusive digital asset to the blockchain.'}
          </p>
          {mintingState.status === 'polling' && mintingState.errorMessage && (
            <p className="text-yellow-600 mt-2 text-sm">{mintingState.errorMessage}</p>
          )}
        </div>
      </div>
    </div>
  );
}

interface PoapRecipient {
  type: "ETH" | "Email";
  value: string;
}

/**
 * Resolve where this user's POAP will go, matching the server's claim-time
 * priority (ethAddress > poapEmail > account email). `/api/user/profile`
 * returns the user fields at the top level — read them directly.
 */
function usePoapRecipient(enabled: boolean): PoapRecipient | null {
  const [recipient, setRecipient] = useState<PoapRecipient | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/user/profile");
        if (!res.ok) return;
        const data = await res.json();
        let next: PoapRecipient | null = null;
        if (data.ethAddress) {
          next = { type: "ETH", value: `${data.ethAddress.slice(0, 6)}...${data.ethAddress.slice(-4)}` };
        } else if (data.poapEmail) {
          next = { type: "Email", value: data.poapEmail };
        } else if (data.email) {
          next = { type: "Email", value: data.email };
        }
        if (!cancelled) setRecipient(next);
      } catch {
        // silently fail — recipient display is non-critical
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return recipient;
}

function PoapRecipientIndicator() {
  const recipient = usePoapRecipient(true);
  if (!recipient) return null;

  return (
    <div className="mt-3 flex items-center justify-center gap-1 text-xs text-gray-500">
      <span>Claiming to: <span className="font-medium text-gray-700">{recipient.value}</span></span>
      <span className="mx-1">·</span>
      <Link href="/dashboard/settings?tab=blockchains" className="text-blue-600 hover:text-blue-800 underline">
        Change
      </Link>
    </div>
  );
}

interface ReadyToMintStatusProps {
  onMint: () => void;
  assetImage?: string;
  assetVideo?: string;
  assetTitle: string;
  primaryColor?: string;
  chain?: string;
}

function ReadyToMintStatus({ onMint, assetImage, assetVideo, assetTitle, primaryColor, chain }: ReadyToMintStatusProps) {
  const isPoap = chain === 'POAP';
  // Button styling - use primary color if provided, otherwise default gradient
  const buttonStyle = primaryColor
    ? { backgroundColor: primaryColor }
    : undefined;
  const buttonClassName = primaryColor
    ? "px-8 py-4 text-white rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-lg hover:opacity-90"
    : "px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg";

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="text-center">
        <AssetCard
          message="You've successfully verified your location!"
          assetImage={assetImage}
          assetVideo={assetVideo}
          assetTitle={assetTitle}
          isPoap={isPoap}
        />
        <div className="mt-6">
          <button
            onClick={onMint}
            className={buttonClassName}
            style={buttonStyle}
          >
            {isPoap ? 'Claim POAP' : 'Mint Digital Asset'}
          </button>
          <p className="mt-3 text-sm text-gray-500">
            {isPoap
              ? 'Click to claim your POAP collectible'
              : 'Click to mint your exclusive NFT to your WAX account'}
          </p>
          {isPoap && <PoapRecipientIndicator />}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * MintingSection renders the appropriate UI based on the minting state.
 *
 * States handled:
 * - success: Shows success message with transaction link
 * - failed: Shows error message with retry button
 * - requires_wallet: Shows wallet connection prompt
 * - claiming/minting/polling: Shows processing animation
 * - idle: Shows mint button (ready state)
 */
export function MintingSection({
  mintingState,
  onMint,
  onRetry,
  onWalletConnected,
  assetImage,
  assetVideo,
  assetTitle,
  primaryColor,
  chain,
}: MintingSectionProps) {
  // Success state
  if (mintingState.status === 'success') {
    return <SuccessStatus mintingState={mintingState} chain={chain} />;
  }

  // Reserved state - claim held for user who needs to link a wallet
  if (mintingState.status === 'reserved') {
    return <ReservedStatus mintingState={mintingState} chain={chain} />;
  }

  // Finalizing state - mint accepted but still settling; show a calm
  // "it'll appear shortly" message rather than an endless spinner.
  if (mintingState.status === 'finalizing') {
    return <FinalizingStatus chain={chain} />;
  }

  // Failed state
  if (mintingState.status === 'failed') {
    return <FailedStatus mintingState={mintingState} onRetry={onRetry} />;
  }

  // Requires wallet state - show inline wallet connection
  if (mintingState.status === 'requires_wallet') {
    if (onWalletConnected) {
      return (
        <WalletConnectionInline
          onWalletConnected={onWalletConnected}
          primaryColor={primaryColor}
        />
      );
    }
    return <WalletRequiredStatus mintingState={mintingState} />;
  }

  // Processing states (claiming, minting, polling)
  if (mintingState.status === 'claiming' || mintingState.status === 'minting' || mintingState.status === 'polling') {
    return (
      <ProcessingStatus
        mintingState={mintingState}
        assetImage={assetImage}
        assetVideo={assetVideo}
        assetTitle={assetTitle}
        chain={chain}
      />
    );
  }

  // Ready to mint (idle state)
  return (
    <ReadyToMintStatus
      onMint={onMint}
      assetImage={assetImage}
      assetVideo={assetVideo}
      assetTitle={assetTitle}
      primaryColor={primaryColor}
      chain={chain}
    />
  );
}
